import React from 'react';
import { TEXT_STYLE_PRESETS, DEFAULT_CAPTION_STYLE } from '../../../utils/textStylePresets';
import CaptionStylePopup from './CaptionStylePopup';
import CaptionPresetButton from './CaptionPresetButton';
import { ensureFontLoaded } from '../../../utils/ensureFontLoaded';

const CaptionControlsSection = ({
  element,
  value,
  index,
  fieldType,
  mediaCategory,
  isGeneratingCaption,
  transcriptInput,
  setTranscriptInput,
  isTranscriptFocused,
  setIsTranscriptFocused,
  transcriptTextareaRef,
  isCaptionPopupOpen,
  setIsCaptionPopupOpen,
  captionPopupAnchorRef,
  onGenerateCaptions,
  onUpdateCaptionStyle,
  onTranscriptChange,
}) => {
  const existingCaptions = element.componentData?.captions;

  return (
    <div className="mt-3 space-y-3">
      <div className="p-3 bg-ag-black-800 rounded-lg border border-ag-black-600">
        <details open={!!existingCaptions}>
          <summary className="text-xs font-medium text-white cursor-pointer select-none flex items-center gap-1.5">
            <span>Transcript</span>
            <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </summary>

          <div className="mt-2 space-y-1.5">
            {existingCaptions && (
              <div className="flex gap-2 mb-2 ml-1">
                <div className="text-[11px] text-gray-400 bg-[#1e1e1e] border border-gray-700 px-2 py-0.5 rounded-md truncate max-w-[140px]">
                  {element.name || 'Media'}
                </div>
                <div className="text-[11px] text-gray-400 bg-[#1e1e1e] border border-gray-700 px-2 py-0.5 rounded-md">
                  {element.timeline?.inPoint !== undefined && element.timeline?.outPoint !== undefined
                    ? `${(element.timeline.inPoint / 25).toFixed(2)} - ${(element.timeline.outPoint / 25).toFixed(1)}s`
                    : '0.00 - 0.0s'}
                </div>
              </div>
            )}

            {existingCaptions && (
              <div className="max-h-[280px] overflow-y-auto timeline-scroll override-scroll pr-2">
                <div
                  className={`flex flex-col rounded-lg overflow-hidden transition-all ${
                    isTranscriptFocused
                      ? 'ring-1 ring-[#3b82f6] border border-[#3b82f6]'
                      : 'border border-transparent'
                  } bg-[#555555]`}
                >
                  <textarea
                    ref={transcriptTextareaRef}
                    style={{ minHeight: '60px' }}
                    value={
                      isTranscriptFocused
                        ? transcriptInput
                        : existingCaptions.phrases.map((phrase) => phrase.text).join(' ')
                    }
                    onFocus={() => {
                      setIsTranscriptFocused(true);
                      setTranscriptInput(existingCaptions.phrases.map((phrase) => phrase.text).join(' '));
                    }}
                    onChange={(event) => {
                      setTranscriptInput(event.target.value);
                      onTranscriptChange(event.target.value);
                    }}
                    onBlur={() => {
                      setTranscriptInput('');
                      setIsTranscriptFocused(false);
                    }}
                    rows={1}
                    className="w-full pl-4 pr-4 py-3 text-[13px] bg-transparent border-none text-white focus:outline-none resize-none leading-[1.7] overflow-y-hidden tracking-wide"
                  />
                </div>
              </div>
            )}
          </div>
        </details>

        <button
          onClick={() => onGenerateCaptions(value, index, fieldType, mediaCategory)}
          disabled={!value || isGeneratingCaption}
          className={`mt-3 px-3 py-0.5 text-[10px] font-medium rounded-full transition-colors flex items-center justify-center gap-1.5 w-max
            ${
              !value || isGeneratingCaption
                ? 'bg-ag-black-600 text-gray-400 cursor-not-allowed'
                : 'bg-[#d4f391] hover:bg-[#c3e88d] text-black'
            }`}
        >
          {isGeneratingCaption ? (
            <>
              <div className="w-3 h-3 border-2 border-gray-400 border-t-black rounded-full animate-spin" />
              Generating...
            </>
          ) : existingCaptions ? (
            'Re-generate'
          ) : (
            'Generate'
          )}
        </button>
      </div>

      {existingCaptions && (
        <div className="p-3 bg-ag-black-800 rounded-lg border border-ag-black-600 relative overflow-visible">
          {isCaptionPopupOpen && (
            <CaptionStylePopup
              currentStyle={{ ...DEFAULT_CAPTION_STYLE, ...(existingCaptions.style || {}) }}
              onChange={onUpdateCaptionStyle}
              onClose={() => setIsCaptionPopupOpen(false)}
              anchorRef={captionPopupAnchorRef}
              mode="caption"
            />
          )}

          <details>
            <summary
              ref={captionPopupAnchorRef}
              className="text-xs font-medium text-white cursor-pointer select-none flex items-center justify-between"
            >
              <div className="flex items-center gap-1">
                <span>Caption</span>
                <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
              <button
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  setIsCaptionPopupOpen(!isCaptionPopupOpen);
                }}
                className="hover:bg-gray-700 p-0.5 rounded transition-colors text-white focus:outline-none"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                  <circle cx="5" cy="12" r="2" />
                  <circle cx="12" cy="12" r="2" />
                  <circle cx="19" cy="12" r="2" />
                </svg>
              </button>
            </summary>

            <div className="mt-2 grid grid-cols-2 gap-x-2 gap-y-3 max-h-[300px] overflow-y-auto timeline-scroll override-scroll pr-1">
              {TEXT_STYLE_PRESETS.map((preset) => (
                <CaptionPresetButton
                  key={preset.key}
                  preset={preset}
                  updateStyle={async (selectedPreset) => {
                    await ensureFontLoaded(selectedPreset.fontFamily);
                    const { key, label, category, bg, ...styleProps } = selectedPreset;
                    onUpdateCaptionStyle(styleProps, true);
                  }}
                />
              ))}
            </div>
          </details>
        </div>
      )}
    </div>
  );
};

export default CaptionControlsSection;
